import { Box, styled, useTheme } from '@mui/material';

import {
  GlowField,
  ORGAN_PROGRESS_COLORS,
  capitalizeWords,
  getIsItalyRegionId,
  parseItalianState,
  useOverflowMask,
} from '#shared';

import { ENOPCaseStatus } from '../../enums';

import type { ActiveCase } from '../../types';

interface Props {
  case: ActiveCase;
  highlightDurationMilliseconds?: number;
}

const PREFIX = 'TSMED-ActiveCaseCardContentCompact';

const activeCaseCardContentCompactClasses = {
  from: `${PREFIX}-from`,
  city: `${PREFIX}-city`,
  state: `${PREFIX}-state`,
  txCode: `${PREFIX}-txCode`,
};
type RootProps = {
  isReturningStatus?: boolean;
};

const excludedProps: PropertyKey[] = ['isReturningStatus'];

const Root = styled(Box, {
  shouldForwardProp: prop => !excludedProps.includes(prop),
})<RootProps>(({ theme: { spacing, palette, utils }, isReturningStatus }) => ({
  display: 'flex',
  fontSize: utils.clamp(12, 14),
  gap: spacing(1),
  [`.${activeCaseCardContentCompactClasses.from}`]: {
    color: isReturningStatus ? '#87888b' : palette.grey[100],
    display: 'flex',
    minWidth: 0,
    gap: spacing(0.5),

    [`.${activeCaseCardContentCompactClasses.city}`]: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      position: 'relative',
      maxWidth: '100%',
      textOverflow: 'clip',
      '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: 0,
        width: 5,
        height: '100%',
        background:
          'linear-gradient(90deg, rgba(15, 21, 28, 0) 0%, #0F151C 100%)',
        pointerEvents: 'none',
        display: 'none',
      },

      // show fade only when overflow is detected
      '&[data-fade="1"]:after': {
        display: 'block',
      },
    },
    [`.${activeCaseCardContentCompactClasses.state}`]: {},
  },
  [`.${activeCaseCardContentCompactClasses.txCode}`]: {
    flexGrow: 1,
    textAlign: 'right',
    color: isReturningStatus ? '#87888b' : 'inherit',
  },
}));

export const ActiveCaseCardContentCompact = (props: Props) => {
  const {
    donorHospital,
    transplantCenterCode,
    organ,
    status,
    serviceRegionId,
  } = props.case;
  const [city, state] = (donorHospital?.address ?? '---, ---').split(', ');
  const txHospitalCode = transplantCenterCode;
  const { palette } = useTheme();
  const isReturningStatus = status === ENOPCaseStatus.ReturningToBase;
  const cityMask = useOverflowMask<HTMLDivElement>([city, state]);
  const isItalian = getIsItalyRegionId(serviceRegionId ?? '');
  const stateToRender = isItalian
    ? parseItalianState(state)
    : state.toUpperCase();

  return (
    <Root isReturningStatus={isReturningStatus}>
      <Box className={activeCaseCardContentCompactClasses.from}>
        <Box
          ref={cityMask.ref}
          data-fade={cityMask.needsFade ? 1 : 0}
          className={activeCaseCardContentCompactClasses.city}
        >
          <GlowField
            component="span"
            startColor={palette.grey[100]}
            deps={[city, stateToRender]}
          >
            {capitalizeWords(city)}
          </GlowField>
        </Box>
        <Box className={activeCaseCardContentCompactClasses.state}>
          <GlowField
            component="span"
            startColor={palette.grey[100]}
            deps={[city, stateToRender]}
          >
            ,{stateToRender}
          </GlowField>
        </Box>
      </Box>
      <GlowField
        startColor={ORGAN_PROGRESS_COLORS[organ.name]}
        deps={[txHospitalCode]}
        className={activeCaseCardContentCompactClasses.txCode}
        ms={props.highlightDurationMilliseconds}
      >
        {txHospitalCode}
      </GlowField>
    </Root>
  );
};
